import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Phone, Sun, Moon, Shield, LogOut, CalendarCheck, LogIn } from "lucide-react";
import { Button } from "../ui/button";
import { HOTEL } from "@/data/site";
import { useAuth } from "@/lib/auth";
import { LoginDialog } from "./LoginDialog";

const NAV = [
  { label: "Rooms", id: "rooms" },
  { label: "Amenities", id: "amenities" },
  { label: "Gallery", id: "gallery" },
  { label: "Location", id: "location" },
  { label: "Reviews", id: "reviews" },
];

export const Header = ({ dark, toggleTheme }) => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();

  const scrollTo = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="sticky top-0 z-50 backdrop-blur-xl bg-background/85 border-b border-primary/10 shadow-sm"
      data-testid="site-header"
    >
      <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto flex items-center justify-between h-16 lg:h-20">
        <button onClick={() => scrollTo("top")} className="text-left" data-testid="header-logo">
          <div className="font-serif font-bold text-lg lg:text-xl leading-none text-primary">Hotel Vijayshree Inn</div>
          <div className="text-[11px] lg:text-xs text-muted-foreground tracking-wide">{HOTEL.nameHindi} · Wardha Road, Nagpur</div>
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => scrollTo(n.id)}
              data-testid={`nav-link-${n.id}`}
              className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            className="p-2 rounded-full hover:bg-accent transition-colors"
            aria-label="Toggle theme"
          >
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-link-admin">
              <Button variant="ghost" size="sm" className="hidden sm:inline-flex gap-1.5">
                <Shield className="h-4 w-4" /> Admin
              </Button>
            </Link>
          )}
          {user ? (
            <div className="hidden sm:flex items-center gap-2" data-testid="header-user-menu">
              <Link to="/my-bookings" data-testid="nav-link-my-bookings">
                <Button variant="ghost" size="sm" className="gap-1.5"><CalendarCheck className="h-4 w-4" /> My Bookings</Button>
              </Link>
              {user.picture ? <img src={user.picture} alt="" className="h-7 w-7 rounded-full" referrerPolicy="no-referrer" />
                : <span className="h-7 w-7 rounded-full bg-primary/15 text-primary text-xs flex items-center justify-center font-semibold">{(user.name || user.email)[0]?.toUpperCase()}</span>}
              <span className="text-xs font-medium max-w-[120px] truncate" data-testid="header-user-name">{user.name || user.email}</span>
              <button onClick={logout} className="p-1.5 rounded-full hover:bg-accent" aria-label="Sign out" data-testid="header-logout-button"><LogOut className="h-4 w-4" /></button>
            </div>
          ) : (
            <LoginDialog trigger={(openFn) => (
              <Button onClick={openFn} variant="outline" size="sm" className="hidden sm:inline-flex h-9 rounded-full gap-1.5" data-testid="header-signin-button">
                <LogIn className="h-4 w-4" /> Sign in
              </Button>
            )} />
          )}
          <a href={`tel:${HOTEL.phoneRaw}`} data-testid="header-call-button">
            <Button size="sm" className="gap-1.5 rounded-full bg-primary hover:bg-primary/90">
              <Phone className="h-4 w-4" /> <span className="hidden sm:inline">Call Now</span>
            </Button>
          </a>
          <button className="lg:hidden p-2" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-background px-4 py-3 flex flex-col gap-1">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => scrollTo(n.id)} className="text-left py-2 text-sm font-medium">
              {n.label}
            </button>
          ))}
          {user?.role === "admin" && <Link to="/admin" className="py-2 text-sm font-medium text-primary">Admin Portal</Link>}
          {user ? (
            <>
              <Link to="/my-bookings" className="py-2 text-sm font-medium" data-testid="mobile-nav-my-bookings">My Bookings</Link>
              <button onClick={logout} className="text-left py-2 text-sm font-medium text-muted-foreground" data-testid="mobile-logout-button">Sign out ({user.email})</button>
            </>
          ) : (
            <LoginDialog trigger={(openFn) => (
              <Button onClick={() => { setOpen(false); openFn(); }} variant="outline" className="mt-1 w-full gap-1.5" data-testid="mobile-signin-button">
                <LogIn className="h-4 w-4" /> Sign in
              </Button>
            )} />
          )}
        </div>
      )}
    </motion.header>
  );
};
